import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import fileDownload from 'js-file-download'
import {
  DashboardBulkDataPayload,
  DashboardCompanyTaxSavingsByPeriod,
  DashboardLocationCard,
  DashboardLocationDetail,
  DashboardMetric,
  DashboardMetricType,
  DashboardPeriod,
  DashboardProgramCard,
  DashboardProgramDetail,
  DashboardProgramEmployee,
  DashboardProgramStats,
  DashboardProgramSpend,
  DashboardProgramStatsByPeriod,
  DashboardTracker,
  DashboardWorksiteDistribution,
  TransitException,
} from './dashboardTypes'

export interface DashboardState {
  metrics: Dictionary<DashboardMetric>
  companyTaxSavings: DashboardCompanyTaxSavingsByPeriod | null
  topServices: DashboardProgramSpend[] | null
  trackers: DashboardTracker
  programStats: DashboardProgramStatsByPeriod
  programCards: DashboardProgramCard[] | null
  locationCards: DashboardLocationCard[] | null
  worksites: DashboardWorksiteDistribution[] | null
  locationDetail: DashboardLocationDetail | null
  locationDetailLoading: boolean
  locationDetailError: boolean
  programDetail: DashboardProgramDetail | null
  programDetailLoading: boolean
  programDetailError: boolean
  transitExceptions: TransitException[] | null
}

const initialState: DashboardState = {
  metrics: null,
  companyTaxSavings: null,
  topServices: null,
  trackers: null,
  programStats: null,
  programCards: null,
  locationCards: null,
  worksites: null,
  locationDetail: null,
  locationDetailLoading: false,
  locationDetailError: false,
  programDetail: null,
  programDetailLoading: false,
  programDetailError: false,
  transitExceptions: null,
}

const emptyPeriodMetrics = { moneySpentCents: 0, enrolledDelta: 0 }

const emptyProgramMetric = { current: 0, previous: 0 }

const emptyProgramStats: DashboardProgramStats = {
  enrolledEmployees: emptyProgramMetric,
  activeCards: emptyProgramMetric,
  pendingActivation: emptyProgramMetric,
  moneySpentCents: emptyProgramMetric,
}

const emptySpendByPeriod: DashboardProgramEmployee['spendByPeriod'] = {
  7: 0,
  30: 0,
  90: 0,
  all: 0,
}

const normalizeSpendByPeriod = (
  spend: DashboardProgramEmployee['spendByPeriod'] & Record<string, number>
): DashboardProgramEmployee['spendByPeriod'] => ({
  7: spend?.[7] ?? spend?.['7'] ?? 0,
  30: spend?.[30] ?? spend?.['30'] ?? 0,
  90: spend?.[90] ?? spend?.['90'] ?? 0,
  all: spend?.all ?? spend?.['all'] ?? 0,
})

const normalizeProgramStatsByPeriod = (
  byPeriod: DashboardProgramStatsByPeriod &
    Record<string, DashboardProgramStats>
): DashboardProgramStatsByPeriod => ({
  7: byPeriod[7] ?? byPeriod['7'] ?? emptyProgramStats,
  30: byPeriod[30] ?? byPeriod['30'] ?? emptyProgramStats,
  90: byPeriod[90] ?? byPeriod['90'] ?? emptyProgramStats,
  all: byPeriod.all ?? byPeriod['all'] ?? emptyProgramStats,
})

const normalizeProgramCardPeriod = (
  byPeriod: DashboardProgramCard['byPeriod'] &
    Record<string, DashboardProgramCard['byPeriod'][DashboardPeriod]>
): DashboardProgramCard['byPeriod'] => ({
  7: byPeriod[7] ?? byPeriod['7'] ?? emptyPeriodMetrics,
  30: byPeriod[30] ?? byPeriod['30'] ?? emptyPeriodMetrics,
  90: byPeriod[90] ?? byPeriod['90'] ?? emptyPeriodMetrics,
  all: byPeriod.all ?? byPeriod['all'] ?? emptyPeriodMetrics,
})

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    setDashboardBulkDataSuccess(
      state,
      action: PayloadAction<DashboardBulkDataPayload>
    ) {
      const metrics = action.payload.metrics

      for (const metric in metrics) {
        metrics[metric].type = metric as DashboardMetricType
        if (metric === 'timeSaved') {
          metrics.timeSaved = {
            type: 'timeSaved',
            currentMonth: metrics.timeSaved.currentMonth / 3600,
            lastMonth: metrics.timeSaved.lastMonth / 3600,
            percentDiff: metrics.timeSaved.percentDiff,
          }
        }
      }

      state.topServices = action.payload.topServices ?? []
      state.metrics = metrics
      const taxSavings = (
        metrics as Dictionary<DashboardMetric> & {
          companyTaxSavings?: DashboardCompanyTaxSavingsByPeriod &
            Record<string, DashboardCompanyTaxSavingsByPeriod[DashboardPeriod]>
        }
      ).companyTaxSavings
      state.companyTaxSavings = taxSavings
        ? {
            7: taxSavings[7] ?? taxSavings['7'],
            30: taxSavings[30] ?? taxSavings['30'],
            90: taxSavings[90] ?? taxSavings['90'],
            all: taxSavings.all ?? taxSavings['all'],
          }
        : null
    },
    setDashboardTrackersSuccess(
      state,
      action: PayloadAction<DashboardTracker>
    ) {
      const payload = action.payload as DashboardTracker &
        Record<string, DashboardTracker[DashboardPeriod]>
      state.trackers = {
        7: payload[7] ?? payload['7'],
        30: payload[30] ?? payload['30'],
        90: payload[90] ?? payload['90'],
        all: payload.all ?? payload['all'],
      }
    },
    setDashboardProgramStatsSuccess(
      state,
      action: PayloadAction<DashboardProgramStatsByPeriod>
    ) {
      // API JSON keys arrive as strings ("7"/"30"/"90"); normalize to numeric keys.
      const payload = action.payload as DashboardProgramStatsByPeriod &
        Record<string, DashboardProgramStatsByPeriod[DashboardPeriod]>
      state.programStats = {
        7: payload[7] ?? payload['7'],
        30: payload[30] ?? payload['30'],
        90: payload[90] ?? payload['90'],
        all: payload.all ?? payload['all'],
      }
    },
    setDashboardProgramCardsSuccess(
      state,
      action: PayloadAction<DashboardProgramCard[]>
    ) {
      // Normalize by_period keys from API strings ("7"/"30"/"90") to numbers.
      state.programCards = (action.payload || []).map((card) => {
        const byPeriod = (card.byPeriod || {}) as DashboardProgramCard['byPeriod'] &
          Record<string, DashboardProgramCard['byPeriod'][DashboardPeriod]>
        return {
          ...card,
          byPeriod: normalizeProgramCardPeriod(byPeriod),
        }
      })
    },
    setDashboardLocationCardsSuccess(
      state,
      action: PayloadAction<DashboardLocationCard[]>
    ) {
      state.locationCards = action.payload || []
    },
    setDashboardWorksitesSuccess(
      state,
      action: PayloadAction<DashboardWorksiteDistribution[]>
    ) {
      state.worksites = action.payload || []
    },
    setDashboardLocationDetailLoading(state) {
      state.locationDetailLoading = true
      state.locationDetailError = false
      state.locationDetail = null
    },
    setDashboardLocationDetailSuccess(
      state,
      action: PayloadAction<DashboardLocationDetail>
    ) {
      state.locationDetail = action.payload
      state.locationDetailLoading = false
      state.locationDetailError = false
    },
    setDashboardLocationDetailFailed(state) {
      state.locationDetail = null
      state.locationDetailLoading = false
      state.locationDetailError = true
    },
    clearDashboardLocationDetail(state) {
      state.locationDetail = null
      state.locationDetailLoading = false
      state.locationDetailError = false
    },
    setDashboardProgramDetailLoading(state) {
      state.programDetailLoading = true
      state.programDetailError = false
      state.programDetail = null
    },
    setDashboardProgramDetailSuccess(
      state,
      action: PayloadAction<DashboardProgramDetail>
    ) {
      const byPeriod = (action.payload.byPeriod ||
        {}) as DashboardProgramStatsByPeriod &
        Record<string, DashboardProgramStats>
      state.programDetail = {
        ...action.payload,
        employees: (action.payload.employees || []).map((employee) => ({
          ...employee,
          spendByPeriod: normalizeSpendByPeriod(
            (employee.spendByPeriod || emptySpendByPeriod) as DashboardProgramEmployee['spendByPeriod'] &
              Record<string, number>
          ),
          transitPass: employee.transitPass
            ? {
                ...employee.transitPass,
                topupsByPeriod: normalizeSpendByPeriod(
                  (employee.transitPass.topupsByPeriod ||
                    emptySpendByPeriod) as DashboardProgramEmployee['spendByPeriod'] &
                    Record<string, number>
                ),
              }
            : null,
        })),
        transitAgencies: action.payload.transitAgencies || [],
        byPeriod: normalizeProgramStatsByPeriod(byPeriod),
      }
      state.programDetailLoading = false
      state.programDetailError = false
    },
    setDashboardProgramDetailFailed(state) {
      state.programDetail = null
      state.programDetailLoading = false
      state.programDetailError = true
    },
    clearDashboardProgramDetail(state) {
      state.programDetail = null
      state.programDetailLoading = false
      state.programDetailError = false
    },
    setDashboardTransitExceptionsSuccess(
      state,
      action: PayloadAction<TransitException[]>
    ) {
      state.transitExceptions = action.payload || []
    },
    updateDashboardTransitExceptionSuccess(
      state,
      action: PayloadAction<TransitException>
    ) {
      if (!state.transitExceptions) {
        state.transitExceptions = [action.payload]
        return
      }
      state.transitExceptions = state.transitExceptions.map((exception) =>
        exception.id === action.payload.id ? action.payload : exception
      )
    },
  },
})

export const dashboardReducer = dashboardSlice.reducer
export const dashboardReducers = {
  ...dashboardSlice.actions,
}
